import {BasketTracker} from '../../../../../../../packages/core/grpc-clients/tadarida/aysa_api/features/tracker/v1/tracker.types';
import {
  BasketAction,
  TrackingBasicElementContext,
  TrackingBasket,
  TrackingBasketItemWithoutPrices,
  TrackingSectionContext,
} from '../../../types';
import {getCategory} from '../helpers/category';
import {
  DataLayerProductGA3,
  DataLayerProductGA4,
  DetailedPageInfo,
  EventPageData,
  InitialPageInfo,
  TrackingClientState,
  TrackingClientStateHolder,
} from '../helpers/clientState';
import {
  DataLayerEvent,
  shouldPushToDataLayerObject,
} from '../helpers/datalayer';
import {debugWarn} from '../helpers/log';
import {
  MappedDimensionsProduct,
  getStateFullDataLayerProduct,
} from '../helpers/product';
import {
  CommonEventData,
  commonEventDataHandler,
} from '../helpers/productListingInteractionHelpers';
import {trackBasket} from './trackBasket';

interface AddToBasketEvent extends DataLayerEvent {
  event: 'addToBasket';
  ecommerce: {
    currencyCode: string;
    add: {
      products: MappedDimensionsProduct<DataLayerProductGA3>[];
    };
  };
  basket: TrackingBasket;
}
interface RemoveFromBasketEvent extends DataLayerEvent {
  event: 'removeFromBasket';
  ecommerce: {
    currencyCode: string;
    remove: {
      products: MappedDimensionsProduct<DataLayerProductGA3>[];
    };
  };
  basket: TrackingBasket;
}

interface AddToBasketEventGA4 extends EventPageData {
  event: 'add_to_cart';
  ecommerce: {
    items: DataLayerProductGA4[];
  };
}
interface RemoveFromBasketEventGA4 extends EventPageData {
  event: 'remove_from_cart';
  ecommerce: {
    items: DataLayerProductGA4[];
  };
}

export const trackBasketAction =
  ({
    type,
    section,
    element,
    basketItem,
    basketTracker,
  }: {
    type: 'add-to-basket' | 'quantity-update' | 'removed-from-basket';
    section: TrackingSectionContext;
    element: TrackingBasicElementContext;
    basketItem: TrackingBasketItemWithoutPrices;
    basketTracker?: BasketTracker;
  }) =>
  (clientState: TrackingClientStateHolder): undefined => {
    const currentState = clientState.getCurrent();
    const {currentPage} = currentState;

    if (!currentPage || currentPage.type === 'initial') {
      debugWarn('tried to track basket action before page view');
      return;
    }

    const basketAction = {
      type,
      basketItem,
      page: currentPage,
      section,
      element,
      basketTracker,
    };

    /**
     * For `quantity-update` type always Add the tracking action to `clientState.queuedBasketActions`.
     * `trackCustomerData` has not happened yet after the "add/remove" event in the shop application.
     * Tracking will be executed once the `trackCustomerData` happens moments after successful add/remove in the shop.
     */
    if (type === 'quantity-update') {
      clientState.update({
        queuedBasketActions: [
          ...currentState.queuedBasketActions,
          basketAction,
        ],
      });
      return; //= > exit after queuing
    }

    /**
     * Other action types (add-to-basket / removed-from-basket) can be executed right away
     * since they must be called called after successful execution of CTA to basket api
     */
    executeTrackBasketAction(clientState)(basketAction);
  };
export const executeTrackBasketAction =
  (clientState: TrackingClientStateHolder) =>
  ({basketItem, type, element, section, basketTracker}: BasketAction) => {
    const currentState = clientState.getCurrent();
    const {currentPage} = currentState;

    if (!currentPage || currentPage.type === 'initial') {
      debugWarn('tried to track basket action before page view');
      return;
    }

    let quantityChanged = 0;

    const currentBasketItemQuantity =
      currentState.basketItems.find(
        item => item.sizeId === basketItem.variant.id
      )?.quantity ?? 0;

    if (type === 'add-to-basket') {
      quantityChanged = 1; //= > always 1
    } else if (type === 'removed-from-basket') {
      quantityChanged = basketItem.selectedQuantity; //= > always the current selectedQuantity of the item
    } else {
      //= > if type === 'quantity-update'
      if (currentBasketItemQuantity === basketItem.selectedQuantity) {
        // quantity-update failed in the shop application?
        return;
      }
      quantityChanged = Math.abs(
        currentBasketItemQuantity - basketItem.selectedQuantity
      );
    }

    const isAddAction =
      type === 'add-to-basket' ||
      (type === 'quantity-update' &&
        currentBasketItemQuantity > basketItem.selectedQuantity);

    const product = basketItem.product;
    const category = getCategory({
      productCategories: null,
      state: currentState,
    });
    const basketTrackerItem = basketTracker?.items.find(
      item => item.size.sizeId === basketItem.variant.id
    );

    const productPrice = product.unitPrice
      ? product.unitPrice
      : {
          ...product.minPrice,
          fullPrice:
            product.minPrice.campaignReductionWithTax +
            product.minPrice.couponReductionWithTax +
            product.minPrice.saleReductionWithTax +
            product.minPrice.withTax,
        };

    const {productGA3, productGA4} = getStateFullDataLayerProduct({
      currentState,
      element,
      product,
      section,
      quantity: quantityChanged,
      variant: basketItem.variant,
      category,
      productUnitPrice: basketTrackerItem
        ? {
            withoutTax: basketTrackerItem.unitPrice?.priceWithoutTax || -1,
            withTax: basketTrackerItem.unitPrice?.price || -1,
            fullPrice: basketTrackerItem.unitPrice?.fullPrice || -1,
            saleReductionWithTax:
              basketTrackerItem.unitPrice?.saleReduction || 0,
            campaignReductionWithTax:
              basketTrackerItem.unitPrice?.campaignReduction || 0,
            couponReductionWithTax:
              basketTrackerItem.unitPrice?.couponReduction || 0,
            currency:
              basketTrackerItem.unitPrice?.currencyCode ||
              '*** currency not available ***',
          }!
        : productPrice,
    });

    const commonEventData = commonEventDataHandler({
      currentPage,
      product,
    });

    const event = createEvent({
      commonEventData,
      products: {
        GA3: [productGA3],
        GA4: [productGA4],
      },
      currentState,
      currentPage,
      basketItem,
      isAddAction,
    });
    const cartItems = basketTracker?.items.map(
      // eslint-disable-next-line complexity
      item =>
        getStateFullDataLayerProduct({
          currentState,
          product: {
            id: item.productId,
            name: item.name,
            url: item.url,
            brand: item.brand,
            colors: item.colors,
            minPrice: {
              withoutTax: item.minPrice?.priceWithoutTax || -1,
              withTax: item.minPrice?.price || -1,
              fullPrice: item.minPrice?.fullPrice || -1,
              saleReductionWithTax: item.minPrice?.saleReduction || 0,
              campaignReductionWithTax: item.minPrice?.campaignReduction || 0,
              couponReductionWithTax: item.minPrice?.couponReduction || 0,
              currency:
                item.minPrice?.currencyCode || '*** currency not available ***',
            },
            isSoldOut: item.isSoldOut,
            isSponsored: item.isSponsored,
            sponsoredType: item.sponsoredType,
            masterKey: item.masterKey || '*** Master key not available ***',
            merchantId: null,
            unitPrice: {
              withoutTax: item.unitPrice?.priceWithoutTax || -1,
              withTax: item.unitPrice?.price || -1,
              fullPrice: item.unitPrice?.fullPrice || -1,
              saleReductionWithTax: item.unitPrice?.saleReduction || 0,
              campaignReductionWithTax: item.unitPrice?.campaignReduction || 0,
              couponReductionWithTax: item.unitPrice?.couponReduction || 0,
              currency:
                item.unitPrice?.currencyCode ||
                '*** currency not available ***',
            },
          },
          productUnitPrice: {
            withoutTax: item.unitPrice?.priceWithoutTax || -1,
            withTax: item.unitPrice?.price || -1,
            fullPrice: item.unitPrice?.fullPrice || -1,
            saleReductionWithTax: item.unitPrice?.saleReduction || 0,
            campaignReductionWithTax: item.unitPrice?.campaignReduction || 0,
            couponReductionWithTax: item.unitPrice?.couponReduction || 0,
            currency:
              item.unitPrice?.currencyCode || '*** currency not available ***',
          },
          category: {
            path: null,
            id: null,
          },
          section,
          element,
        }).productGA4
    );
    const total = currentState.basket.total;
    const trackCart = {
      GA4: trackBasket(cartItems || [], total),
      GA3: undefined,
    };

    shouldPushToDataLayerObject(trackCart, clientState);
    shouldPushToDataLayerObject(event, clientState);
  };

function createEvent({
  commonEventData,
  products,
  currentState,
  currentPage,
  basketItem,
  isAddAction,
}: {
  commonEventData: CommonEventData;
  products: {
    GA3: MappedDimensionsProduct<DataLayerProductGA3>[];
    GA4: DataLayerProductGA4[];
  };
  currentState: TrackingClientState;
  currentPage?: DetailedPageInfo | InitialPageInfo;
  basketItem: TrackingBasketItemWithoutPrices;
  isAddAction: boolean;
}) {
  const eventGA3 = {
    event: isAddAction ? 'addToBasket' : 'removeFromBasket',
    ...commonEventData,
    ecommerce: {
      currencyCode: basketItem.product.minPrice.currency,
      [isAddAction ? 'add' : 'remove']: {
        products: products.GA3,
      },
    },
    basket: currentState.basket,
    ...(currentState.customerDataInitialized && {incomplete: true}),
  } as AddToBasketEvent | RemoveFromBasketEvent;

  const eventGA4 = {
    event: isAddAction ? 'add_to_cart' : 'remove_from_cart',
    content_name: currentPage?.page?.path || '',
    page_type: currentPage?.page?.type || '',
    page_type_id:
      currentPage?.type === 'detailed' ? `${currentPage.page?.id || ''}` : '',
    ecommerce: {
      items: products.GA4,
    },
  } as AddToBasketEventGA4 | RemoveFromBasketEventGA4;

  const event = {
    GA3: eventGA3,
    GA4: eventGA4,
  };

  return event;
}
